import { useState } from "react"
import { Head, router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Eye, EyeOff, ArrowLeft } from "lucide-react"
import logo from "@/assets/logo-25segundos-horizontal.png"

interface Props {
  token: string
  flash?: {
    notice?: string
    alert?: string
  }
  errors?: {
    password?: string[]
    password_confirmation?: string[]
    base?: string[]
  }
}

export default function ResetPassword({ token, flash, errors }: Props) {
  const [password, setPassword] = useState("")
  const [passwordConfirmation, setPasswordConfirmation] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (password !== passwordConfirmation) {
      alert("As senhas não coincidem")
      return
    }

    if (password.length < 8) {
      alert("A senha deve ter pelo menos 8 caracteres")
      return
    }

    setIsLoading(true)

    router.patch(`/redefinir-senha/${token}`, {
      password,
      password_confirmation: passwordConfirmation
    }, {
      onFinish: () => setIsLoading(false)
    })
  }

  return (
    <>
      <Head title="Redefinir senha - Orientação Profissional" />

      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-4">
            <div className="flex justify-center">
              <img src={logo} alt="25Segundos" className="h-16" />
            </div>
            <div>
              <CardTitle className="text-2xl text-center">Redefinir senha</CardTitle>
              <p className="text-center text-muted-foreground mt-2">
                Digite sua nova senha
              </p>
            </div>
          </CardHeader>

          <CardContent>
            {flash?.notice && (
              <div className="mb-4 p-4 bg-green-100 text-green-800 rounded-lg text-sm">
                {flash.notice}
              </div>
            )}

            {flash?.alert && (
              <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-lg text-sm">
                {flash.alert}
              </div>
            )}

            {errors?.base && (
              <div className="mb-4 p-4 bg-red-100 text-red-800 rounded-lg text-sm">
                {errors.base[0]}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Nova senha</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="pr-10"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors?.password && (
                  <p className="text-sm text-red-500">{errors.password[0]}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="password_confirmation">Confirmar nova senha</Label>
                <div className="relative">
                  <Input
                    id="password_confirmation"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    required
                    minLength={8}
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {errors?.password_confirmation && (
                  <p className="text-sm text-red-500">{errors.password_confirmation[0]}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-gradient-primary hover:opacity-90 transition-opacity"
                disabled={isLoading}
              >
                {isLoading ? "Atualizando..." : "Atualizar senha"}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full text-sm"
                onClick={() => router.visit("/login")}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Voltar para login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  )
}
